import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  ClockIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

interface PlaceBusinessHours {
  id: string;
  placeId: string;
  dayOfWeek: number;
  openTime: string;
  closeTime: string;
  isActive: boolean;
  formattedHours: string;
  createdAt: string;
  updatedAt: string;
}

interface PlaceBusinessHoursMatrixProps {
  placeId: string;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Domingo', short: 'Dom' },
  { value: 1, label: 'Segunda-feira', short: 'Seg' },
  { value: 2, label: 'Terça-feira', short: 'Ter' },
  { value: 3, label: 'Quarta-feira', short: 'Qua' },
  { value: 4, label: 'Quinta-feira', short: 'Qui' },
  { value: 5, label: 'Sexta-feira', short: 'Sex' },
  { value: 6, label: 'Sábado', short: 'Sáb' }
];

const PlaceBusinessHoursMatrix: React.FC<PlaceBusinessHoursMatrixProps> = ({ placeId }) => {
  const [editingHours, setEditingHours] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    dayOfWeek: 0,
    openTime: '09:00',
    closeTime: '18:00'
  });

  const queryClient = useQueryClient();

  // Buscar horários de funcionamento
  const { data: businessHours, isLoading } = useQuery({
    queryKey: ['place-business-hours', placeId],
    queryFn: async () => {
      const response = await api.get(`/PlaceBusinessHours/place/${placeId}`);
      return response.data as { hoursByDay: Record<number, PlaceBusinessHours[]> };
    },
    enabled: !!placeId
  });

  // Mutação para criar horário
  const createHoursMutation = useMutation({
    mutationFn: async (data: { dayOfWeek: number; openTime: string; closeTime: string }) => {
      const response = await api.post(`/PlaceBusinessHours/place/${placeId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place-business-hours', placeId] });
      setShowAddForm(null);
      setFormData({ dayOfWeek: 0, openTime: '09:00', closeTime: '18:00' });
    }
  });

  // Mutação para atualizar horário
  const updateHoursMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { openTime: string; closeTime: string; isActive: boolean } }) => {
      const response = await api.put(`/PlaceBusinessHours/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place-business-hours', placeId] });
      setEditingHours(null);
    }
  });

  // Mutação para deletar horário
  const deleteHoursMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/PlaceBusinessHours/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place-business-hours', placeId] });
    }
  });

  const handleAddHours = (dayOfWeek: number) => {
    setFormData({ ...formData, dayOfWeek });
    setShowAddForm(dayOfWeek);
  };

  const handleSubmitAdd = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar sobreposição de horários
    const validation = hasTimeOverlap(formData.dayOfWeek, formData.openTime, formData.closeTime);
    if (validation.hasOverlap) {
      alert(validation.message);
      return;
    }
    
    createHoursMutation.mutate(formData);
  };

  const handleEdit = (hours: PlaceBusinessHours) => {
    setEditingHours(hours.id);
  };

  const handleUpdate = (id: string, data: { openTime: string; closeTime: string }) => {
    updateHoursMutation.mutate({ id, data: { ...data, isActive: true } });
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir este horário?')) {
      deleteHoursMutation.mutate(id);
    }
  };


  // Função para validar se há sobreposição de horários
  const hasTimeOverlap = (dayOfWeek: number, openTime: string, closeTime: string, excludeId?: string) => {
    const dayHours = businessHours?.hoursByDay?.[dayOfWeek] || [];
    
    const newStart = new Date(`2000-01-01T${openTime}`);
    const newEnd = new Date(`2000-01-01T${closeTime}`);
    
    // Verificar se o horário de abertura é menor que o de fechamento
    if (newStart >= newEnd) {
      return { hasOverlap: true, message: 'Horário de abertura deve ser menor que o de fechamento' };
    }
    
    for (const hours of dayHours) {
      if (excludeId && hours.id === excludeId) continue;
      
      const existingStart = new Date(`2000-01-01T${hours.openTime}`);
      const existingEnd = new Date(`2000-01-01T${hours.closeTime}`);
      
      // Verificar sobreposição: novo horário não deve começar antes do existente terminar
      // e não deve terminar depois do existente começar
      if (newStart < existingEnd && newEnd > existingStart) {
        return { 
          hasOverlap: true, 
          message: `Sobreposição com horário existente: ${hours.formattedHours}` 
        };
      }
    }
    
    return { hasOverlap: false, message: '' };
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Horários de Funcionamento</h3>
        <div className="text-sm text-gray-500">
          Configure os horários de funcionamento para cada dia da semana
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dia da Semana
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Horários
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {DAYS_OF_WEEK.map((day) => {
                 const dayHours = businessHours?.hoursByDay?.[day.value] || [];
                 const isAdding = showAddForm === day.value;
                 const hasHours = dayHours.length > 0;

                return (
                  <tr key={day.value} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {day.label}
                        </span>
                      </div>
                    </td>
                    
                     <td className="px-6 py-4">
                       <div className="space-y-2">
                         {/* Horários existentes */}
                         {hasHours && (
                           <div className="space-y-1">
                             {dayHours.map((hours) => (
                               <div key={hours.id} className="flex items-center space-x-2">
                                 {editingHours === hours.id ? (
                                   <EditHoursForm
                                     hours={hours}
                                     onSave={(data) => handleUpdate(hours.id, data)}
                                     onCancel={() => setEditingHours(null)}
                                     onValidate={hasTimeOverlap}
                                   />
                                 ) : (
                                   <>
                                     <span className="text-sm text-gray-900">
                                       {hours.formattedHours}
                                     </span>
                                     <div className="flex items-center space-x-2">
                                       <button
                                         onClick={() => handleEdit(hours)}
                                         className="flex items-center space-x-1 px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                                         title="Editar horário"
                                       >
                                         <PencilIcon className="h-3 w-3" />
                                         <span className="text-xs font-medium">Editar</span>
                                       </button>
                                       <button
                                         onClick={() => handleDelete(hours.id)}
                                         className="flex items-center space-x-1 px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                                         title="Excluir horário"
                                       >
                                         <TrashIcon className="h-3 w-3" />
                                         <span className="text-xs font-medium">Excluir</span>
                                       </button>
                                     </div>
                                   </>
                                 )}
                               </div>
                             ))}
                           </div>
                         )}
                         
                         {/* Formulário de adicionar */}
                         {isAdding && (
                           <form onSubmit={handleSubmitAdd} className="flex items-center space-x-2">
                             <input
                               type="time"
                               value={formData.openTime}
                               onChange={(e) => setFormData({ ...formData, openTime: e.target.value })}
                               className="px-2 py-1 border border-gray-300 rounded text-sm"
                               required
                             />
                             <span className="text-gray-500">até</span>
                             <input
                               type="time"
                               value={formData.closeTime}
                               onChange={(e) => setFormData({ ...formData, closeTime: e.target.value })}
                               className="px-2 py-1 border border-gray-300 rounded text-sm"
                               required
                             />
                             <button
                               type="submit"
                               disabled={createHoursMutation.isPending}
                               className="p-1 text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
                             >
                               <CheckIcon className="h-4 w-4" />
                             </button>
                             <button
                               type="button"
                               onClick={() => setShowAddForm(null)}
                               className="p-1 text-gray-400 hover:text-gray-600"
                             >
                               <XMarkIcon className="h-4 w-4" />
                             </button>
                           </form>
                         )}
                         
                         {/* Mensagem quando não há horários */}
                         {!hasHours && !isAdding && (
                           <span className="text-sm text-gray-400">Nenhum horário configurado</span>
                         )}
                       </div>
                     </td>
                    
                    
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {!isAdding && (
                          <button
                            onClick={() => handleAddHours(day.value)}
                            className="flex items-center space-x-1 px-3 py-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-md transition-colors"
                            title="Adicionar horário"
                          >
                            <PlusIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">Adicionar</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {DAYS_OF_WEEK.map((day) => {
          const dayHours = businessHours?.hoursByDay?.[day.value] || [];
          const isAdding = showAddForm === day.value;
          const hasHours = dayHours.length > 0;

          return (
            <div key={day.value} className="bg-white rounded-lg border border-gray-200 p-4">
              {/* Header do dia */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-900">
                    {day.label}
                  </span>
                </div>
                {!isAdding && (
                  <button
                    onClick={() => handleAddHours(day.value)}
                    className="flex items-center space-x-1 px-3 py-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-md transition-colors"
                    title="Adicionar horário"
                  >
                    <PlusIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">Adicionar</span>
                  </button>
                )}
              </div>

              {/* Horários */}
              <div className="space-y-3">
                {/* Horários existentes */}
                {hasHours && (
                  <div className="space-y-2">
                    {dayHours.map((hours) => (
                      <div key={hours.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                        {editingHours === hours.id ? (
                          <EditHoursForm
                            hours={hours}
                            onSave={(data) => handleUpdate(hours.id, data)}
                            onCancel={() => setEditingHours(null)}
                            onValidate={hasTimeOverlap}
                          />
                        ) : (
                          <>
                            <span className="text-sm text-gray-900">
                              {hours.formattedHours}
                            </span>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEdit(hours)}
                                className="flex items-center space-x-1 px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                                title="Editar horário"
                              >
                                <PencilIcon className="h-3 w-3" />
                                <span className="text-xs font-medium">Editar</span>
                              </button>
                              <button
                                onClick={() => handleDelete(hours.id)}
                                className="flex items-center space-x-1 px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                                title="Excluir horário"
                              >
                                <TrashIcon className="h-3 w-3" />
                                <span className="text-xs font-medium">Excluir</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Formulário de adicionar */}
                {isAdding && (
                  <form onSubmit={handleSubmitAdd} className="flex flex-col space-y-2 p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center space-x-2">
                      <input
                        type="time"
                        value={formData.openTime}
                        onChange={(e) => setFormData({ ...formData, openTime: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                        required
                      />
                      <span className="text-gray-500">até</span>
                      <input
                        type="time"
                        value={formData.closeTime}
                        onChange={(e) => setFormData({ ...formData, closeTime: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                        required
                      />
                    </div>
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        type="submit"
                        disabled={createHoursMutation.isPending}
                        className="flex items-center space-x-1 px-3 py-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-md transition-colors disabled:opacity-50"
                      >
                        <CheckIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">Salvar</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddForm(null)}
                        className="flex items-center space-x-1 px-3 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">Cancelar</span>
                      </button>
                    </div>
                  </form>
                )}
                
                {/* Mensagem quando não há horários */}
                {!hasHours && !isAdding && (
                  <div className="text-center py-4">
                    <span className="text-sm text-gray-400">Nenhum horário configurado</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Componente para editar horário inline
const EditHoursForm: React.FC<{
  hours: PlaceBusinessHours;
  onSave: (data: { openTime: string; closeTime: string }) => void;
  onCancel: () => void;
  onValidate?: (dayOfWeek: number, openTime: string, closeTime: string, excludeId?: string) => { hasOverlap: boolean; message: string };
}> = ({ hours, onSave, onCancel, onValidate }) => {
  const [formData, setFormData] = useState({
    openTime: hours.openTime,
    closeTime: hours.closeTime
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar sobreposição se a função de validação estiver disponível
    if (onValidate) {
      const validation = onValidate(hours.dayOfWeek, formData.openTime, formData.closeTime, hours.id);
      if (validation.hasOverlap) {
        alert(validation.message);
        return;
      }
    }
    
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center space-x-2">
      <input
        type="time"
        value={formData.openTime}
        onChange={(e) => setFormData({ ...formData, openTime: e.target.value })}
        className="px-2 py-1 border border-gray-300 rounded text-sm"
        required
      />
      <span className="text-gray-500">até</span>
      <input
        type="time"
        value={formData.closeTime}
        onChange={(e) => setFormData({ ...formData, closeTime: e.target.value })}
        className="px-2 py-1 border border-gray-300 rounded text-sm"
        required
      />
      <button
        type="submit"
        className="p-1 text-emerald-600 hover:text-emerald-800"
      >
        <CheckIcon className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="p-1 text-gray-400 hover:text-gray-600"
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
    </form>
  );
};

export default PlaceBusinessHoursMatrix;
